import { useAppSelector } from '@/hooks/useAppSelector';
import {
  downloadAllFacturas,
  downloadFactura,
  getFacturas,
  sendFacturaCustomer,
} from '@/redux/actions/facturasService';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import MUIDataTable from 'mui-datatables';
import { Tooltip, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { getSimboloMoneda } from '@/utils/utils';
import { formatImporte } from '@/utils/money';
import moment from 'moment-timezone';
import { IconButton } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import EmailIcon from '@mui/icons-material/Email';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { Spinner } from '@/components/ui/Spinner';
import { RectificativaButton } from './RectificativaButton';
import { Box } from '@mui/system';
import { FacturasSyncNemonButton } from './FacturasSyncNemonButton';
import InfoIcon from '@mui/icons-material/Info';
import { alpha } from '@mui/material/styles';
import { ROLADMIN, ROLREVENDEDOR } from '@/utils/constants';

interface Props {
  PkVentas?: string;
}

export const FacturasTable = ({ PkVentas }: Props) => {
  const dispatch = useDispatch();
  const { facturas, loading } = useAppSelector(state => state.factura);
  const { menuState } = useAppSelector(state => state.ui);
  const { idRol, moneda: monedaUsuario } = useAppSelector(state => state.auth);

  const { activeEdit: currentSale } = useAppSelector(state => state.invoice);
  const rectificadaACero = currentSale?.rectificadaACero;

  const theme = useTheme();
  const matchesXS = useMediaQuery(theme.breakpoints.down('xs'));
  const matchesMD = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    dispatch(getFacturas(PkVentas));
  }, [PkVentas, dispatch]);

  const handleDownloadFactura = (PkFacturas: number, facturaName?: string) => {
    dispatch(downloadFactura(PkFacturas, facturaName));
  };

  const handleSendMailCustomer = (PkFacturas: number) => {
    dispatch(sendFacturaCustomer(PkFacturas));
  };

  const columns = [
    { name: 'Nombre', label: 'Nombre' },
    { name: 'Fecha', label: 'Fecha' },
    { name: 'Importe', label: `Importe Factura (${getSimboloMoneda(monedaUsuario)})` },
    { name: 'Estado', label: 'Estado' },
    { name: 'FechaEnvio', label: 'Fecha Envío' },
    { name: 'Tipo', label: 'Tipo' },
    { name: 'FechaAlta', label: 'Fecha de alta' },
    { name: 'Comentarios', label: 'Comentarios' },

    {
      name: 'Acciones',
      label: 'Acciones',
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex: number) => {
          const factura = facturas[dataIndex];

          return (
            <Box display={'flex'} flexDirection={'row'} gap={1} justifyContent={'center'}>
              <Tooltip title={idRol === ROLREVENDEDOR ? "La descarga de facturas está deshabilitada en período de pruebas de facturación" : "Descargar factura"} arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled= {idRol === ROLREVENDEDOR}
                    onClick={e => {
                      e.stopPropagation();
                      handleDownloadFactura(factura.PkFacturas, factura.NombreFactura);
                    }}>
                    <GetAppIcon />
                  </IconButton>
                </span>
              </Tooltip>
              
              {idRol !== ROLREVENDEDOR && (

              <Tooltip title="El envío de facturas a cliente está deshabilitado en período de pruebas de facturación" arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={true} // Habilitar en cambio de tenant a facturación real. Cambiar tooltip en caso de habilitar
                    onClick={e => {
                      e.stopPropagation();
                      handleSendMailCustomer(factura.PkFacturas);
                    }}
                  >
                    <EmailIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            </Box>
          );
        },
      },
    },
  ];

  const data = facturas.map(factura => ({
    Nombre: factura.NombreFactura,
    Fecha: factura.FechaFactura ? moment(factura.FechaFactura).format('DD/MM/YYYY') : 'Sin fecha',
    Importe: formatImporte(factura.ImporteFactura, monedaUsuario),
    Estado: factura.EstadoERP,
    FechaEnvio: factura.FechaEnvioFactura ? moment(factura.FechaEnvioFactura).format('DD/MM/YYYY') : 'Sin fecha',
    Tipo: factura.TipoFactura,
    FechaAlta: factura.FechaAlta ? moment(factura.FechaAlta).format('DD/MM/YYYY HH:mm') : 'Sin fecha',
    Comentarios: factura.Comentarios || '-',
  }));

  const handleDownloadAllFacturas = () => {
    dispatch(downloadAllFacturas(PkVentas));
  };

  const options = {
    filter: true,
    filterType: 'textField',
    responsive: 'standard',
    serverSide: false,
    rowsPerPage: 10,
    selectableRows: 'none',
    download: false,
    print: false,
    search: false,
    customToolbar: () => {
      return (
        <Tooltip title="Descargar todas las facturas">
          <IconButton onClick={handleDownloadAllFacturas}>
            <CloudDownloadIcon />
          </IconButton>
        </Tooltip>
      );
    },
  };

  return (
    <div
      style={{
        marginTop: '2rem',
        width: `${matchesXS ? '95vw' : matchesMD ? (menuState ? '55vw' : '85vw') : menuState ? '82vw' : '91vw'}`,
      }}>
      <Typography variant="h2" style={{ marginLeft: '1.7rem' }}>
        FACTURAS
      </Typography>
      {rectificadaACero && (
        <Box
          width={'fit-content'}
          bgcolor={alpha('#F39868', 0.5)}
          borderRadius={2}
          display={'flex'}
          alignItems={'center'}
          gap={1}
          marginTop={4}
          paddingY={1}
          paddingX={2}>
          <InfoIcon />
          <Typography variant="body1" style={{ fontSize: 18 }}>
            La factura ha sido rectificada a 0
          </Typography>
        </Box>
      )}

      {idRol === ROLADMIN && (
        <Box display="flex" mt={4} gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
          <RectificativaButton type="parcial" disabled={rectificadaACero} />
          <RectificativaButton type="total" disabled={rectificadaACero} />
          <FacturasSyncNemonButton />
        </Box>
      )}

      {!facturas && loading ? (
        <Spinner />
      ) : (
        <MUIDataTable title={matchesXS && 'Facturas'} data={data} columns={columns} options={options} />
      )}
    </div>
  );
};
